import React from 'react'
import {
  Avatar,
  Badge,
  ChevronDownIcon,
  cn,
  Paragraph,
  Tooltip,
  TriangleExclamationIcon,
} from '@quickmd-llc/design-system'
import moment from 'moment'
import {
  InPersonComplianceStatusOutContract,
  type WaitingRoomV2VisitListItemDoctorApiOutContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { ProgramVisitBadge } from 'components/program-visit-badge/ProgramVisitBadge'
import { getGrantVisitBadgeLabel, readVisitCoverage } from '@quickmd-llc/frontend-utils'
import { formatVisitTimeOrDate, formatVisitOccurrence } from '../utils'
import { computeHeaderStyles } from '../headerStyles'
import { getAge, formatPatientName } from 'utils/common'
import { DRUG_SCREEN_STATUS, ID_VERIFICATION_STATUSES } from 'utils/constants'
import { type DismissedKind, type JustChangeKind } from '../hooks/useVisitLists'
import './PatientRow.scss'

type Props = {
  visit: WaitingRoomV2VisitListItemDoctorApiOutContract
  onClick?: ((visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => void) | undefined
  isNext?: boolean | undefined
  justChangeKind?: JustChangeKind | undefined
  hasSeenJustChangeFade?: boolean | undefined
  dismissed?:
    | {
        kind: DismissedKind
        newDateTime?: string | undefined
        dismissedAt: number
      }
    | undefined
  onDismiss?: (() => void) | undefined
  trailing?: React.ReactNode | undefined
}

const formatDismissedAtRelative = (dismissedAt: number): string => {
  const diffMs = Math.max(0, Date.now() - dismissedAt)
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ago`
}

const formatRescheduledTo = (newDateTime: string): string => {
  const m = moment(newDateTime).local()
  if (m.isSame(moment(), 'day')) return m.format('h:mma')
  return m.format('M/D/YYYY h:mma')
}

/**
 * Active ID-verification and drug-screen alerts for a patient, surfaced as a single
 * dashboard alert icon with a tooltip listing each issue (QMD-3818).
 */
const getPatientAlertLabels = (patient: WaitingRoomV2VisitListItemDoctorApiOutContract['Patient']): string[] => {
  const alerts: string[] = []
  switch (patient?.IdentityVerification?.Status) {
    case ID_VERIFICATION_STATUSES.failed:
      alerts.push('ID verification failed')
      break
    case ID_VERIFICATION_STATUSES.warning:
      alerts.push('ID verification needs review')
      break
    case ID_VERIFICATION_STATUSES.unverified:
      alerts.push('ID not verified')
      break
    default:
      break
  }
  switch (patient?.DrugScreen?.Status) {
    case DRUG_SCREEN_STATUS.expired:
      alerts.push('Drug screen expired')
      break
    case DRUG_SCREEN_STATUS.due:
      alerts.push('Drug screen overdue')
      break
    case DRUG_SCREEN_STATUS.duesoon:
      alerts.push('Drug screen due soon')
      break
    default:
      break
  }
  switch (patient?.InPersonComplianceStatus) {
    case InPersonComplianceStatusOutContract.overdue:
      alerts.push('In-person visit overdue')
      break
    case InPersonComplianceStatusOutContract.soon:
      alerts.push('In-person visit due soon')
      break
    default:
      break
  }
  return alerts
}

export const PatientRow: React.FC<Props> = ({
  visit,
  onClick,
  isNext = false,
  justChangeKind,
  hasSeenJustChangeFade = false,
  dismissed,
  onDismiss,
  trailing,
}) => {
  const patient = visit.Patient
  const isNewPatient = visit.OccurrenceNumber === 1
  const age = getAge(patient?.BirthDate)
  const gender = patient?.Gender?.charAt(0)?.toUpperCase() ?? ''
  const state = visit.StateCode
  const occurrence = formatVisitOccurrence(visit.OccurrenceNumber)
  const nextHeaderStyles = isNext ? computeHeaderStyles(visit.DateTime) : undefined
  const nextBorderClass = nextHeaderStyles?.borderClass ?? ''
  const nextBgClass = nextHeaderStyles?.bgClass
  const nextBgHex = nextHeaderStyles?.bgHex
  const isDismissed = Boolean(dismissed)
  const isCancelled = dismissed?.kind === 'cancelled'
  const patientAlertLabels = isDismissed ? [] : getPatientAlertLabels(patient)
  // A grant-funded visit surfaces its program name in the same alert tooltip,
  // after the patient-level alerts (QMD-4306). Read off the visit's coverage —
  // the same block the plan chip on this row already uses.
  const grantProgramName = isDismissed ? null : getGrantVisitBadgeLabel(readVisitCoverage(visit))
  const alertLabels = grantProgramName ? [...patientAlertLabels, grantProgramName] : patientAlertLabels
  const isPatientAvailable = Boolean(visit.IsPatientAvailable) && !isDismissed
  const isInteractive = !isDismissed && Boolean(onClick)
  const nextTierStyle: (React.CSSProperties & Record<`--${string}`, string>) | undefined =
    isNext && !isDismissed && nextBgHex ? { '--next-tier-bg': nextBgHex } : undefined

  const dismissedStatusContent =
    dismissed ?
      <Paragraph size='sm' className='font-semibold text-text-warning'>
        &bull; &nbsp;
        {dismissed.kind === 'cancelled' ?
          `Canceled ${formatDismissedAtRelative(dismissed.dismissedAt)}`
        : dismissed.newDateTime ?
          `Rescheduled to ${formatRescheduledTo(dismissed.newDateTime)}`
        : 'Rescheduled'}
      </Paragraph>
    : null

  const handleActivate = () => {
    if (isDismissed) return
    onClick?.(visit)
  }

  return (
    <div
      {...(isInteractive ?
        {
          role: 'button',
          tabIndex: 0,
          onClick: handleActivate,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleActivate()
            }
          },
        }
      : {})}
      className={cn(
        'box-border flex items-center gap-sp-1 border-b border-l-4 border-border-medium px-sp-3',
        isInteractive && 'cursor-pointer transition-colors hover:bg-surface-highlight',
        isNext ? 'py-sp-2' : 'py-sp-1.5',
        !isNext && 'border-l-border-light',
        isNext && nextBorderClass,
        isNext && 'border-b-border-medium',
        isNext && !isDismissed && nextBgClass,
        !isDismissed &&
          justChangeKind &&
          hasSeenJustChangeFade &&
          (isNext ? 'patient-row-just-change-fade-next' : 'patient-row-just-change-fade'),
        isDismissed && 'bg-neutral-5',
      )}
      style={nextTierStyle}>
      <div className='w-[72px] flex-shrink-0'>
        {isNext ?
          <>
            <Paragraph
              className={cn('font-semibold', isDismissed ? 'text-text-disabled line-through' : 'text-text-dark')}>
              Next
            </Paragraph>
            <Paragraph
              className={cn('font-semibold', isDismissed ? 'text-text-disabled line-through' : 'text-text-dark')}>
              {formatVisitTimeOrDate(visit.DateTime)}
            </Paragraph>
          </>
        : <Paragraph className={cn(isDismissed ? 'text-text-disabled line-through' : 'text-text-dark')}>
            {formatVisitTimeOrDate(visit.DateTime)}
          </Paragraph>
        }
      </div>

      <div className={cn('flex min-w-[384px] flex-1 items-center', isNext ? 'gap-sp-2' : 'gap-sp-3 pl-sp-1')}>
        <div className={cn('flex-shrink-0', isDismissed && 'opacity-50')}>
          <Avatar
            name={`${patient?.FirstName ?? ''} ${patient?.LastName ?? ''}`}
            // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
            src={patient?.ImageUrl}
            size={isNext ? 'm' : 'xs'}
          />
        </div>
        <div className='flex flex-col gap-sp-0.25'>
          <div className='flex flex-wrap items-center gap-sp-1'>
            {/* @ts-expect-error TS2375 -- MIGRATION: not approved, fix when encountered */}
            <Paragraph
              className={cn(
                'font-semibold',
                isDismissed ? 'text-text-disabled' : 'text-text-dark',
                isCancelled && 'line-through',
              )}
              size={isNext ? 'lg' : undefined}>
              {formatPatientName(patient, { includePreferred: isNext })}
            </Paragraph>
            {isNewPatient && !isDismissed ?
              <Badge size='s' variant='active'>
                New patient
              </Badge>
            : null}
            {isPatientAvailable ?
              <Badge size='s' variant='success'>
                Patient available
              </Badge>
            : null}
            {/*
              Sits in the name row rather than the detail line below, per Figma: it is a
              status pill about the patient's plan, not another attribute of the visit.
              Suppressed on a dismissed row so a cancelled/deferred visit doesn't keep
              advertising a plan slot it gave back.
            */}
            {isDismissed ? null : <ProgramVisitBadge coverage={readVisitCoverage(visit)} />}
          </div>
          <div className='flex flex-wrap items-center gap-sp-1'>
            <Paragraph
              size='sm'
              className={cn(isDismissed ? 'text-text-disabled' : 'text-text-light', isCancelled && 'line-through')}>
              {age}/{gender}, {state}
            </Paragraph>
            {occurrence && !isDismissed ?
              <Paragraph size='sm' className='text-text-light'>
                &bull; &nbsp;{occurrence}
              </Paragraph>
            : null}
            {justChangeKind && !isDismissed ?
              <Paragraph size='sm' className='font-semibold text-text-success'>
                &bull; &nbsp;
                {justChangeKind === 'just-scheduled' ? 'Just scheduled' : 'Just rescheduled'}
              </Paragraph>
            : null}
            {dismissed && dismissedStatusContent ?
              <>
                {dismissedStatusContent}
                <button
                  type='button'
                  className='cursor-pointer border-0 bg-transparent p-0'
                  onClick={(e) => {
                    e.stopPropagation()
                    onDismiss?.()
                  }}>
                  <Paragraph size='sm' className='font-semibold text-text-dark underline'>
                    Dismiss
                  </Paragraph>
                </button>
              </>
            : null}
          </div>
        </div>
      </div>

      {alertLabels.length > 0 && (
        <Tooltip
          content={
            <div className='flex flex-col gap-sp-0.25'>
              {alertLabels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
          }>
          <span className='flex items-center' role='img' aria-label={alertLabels.join('. ')}>
            <TriangleExclamationIcon iconSize='s' className='text-text-warning' />
          </span>
        </Tooltip>
      )}

      {visit.ServiceTypeAbbreviation ?
        <div
          className={cn(
            'flex items-center rounded-full px-sp-0.75 py-sp-0.25',
            isDismissed ? 'bg-neutral-20' : 'bg-surface-active',
          )}>
          <Paragraph
            size='sm'
            className={cn('font-semibold', isDismissed ? 'text-text-disabled' : 'text-text-highlight')}>
            {visit.ServiceTypeAbbreviation}
          </Paragraph>
        </div>
      : null}

      {trailing ?? (
        <ChevronDownIcon
          className={cn('-rotate-90', isDismissed ? 'text-text-disabled' : 'text-text-medium')}
          iconSize='xs'
        />
      )}
    </div>
  )
}
